import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import { Row, Col, Form, Button } from "react-bootstrap";
import * as Yup from "yup";
import { toast } from "react-toastify";
import useApiClient, { apiRoutes } from "../../../../hooks/apiClient";
import TelaAtividadeAltExec from "./TelaAtividadeAltExec";
import FormAlert from "../../../../components/UI/FormAlert/FormAlert";
import useExcluirAtividadeAltExec from "../hooks/useExcluirAtividadeAltExec";
import useAssinarAtividadeAltExec from "../hooks/useAssinarAtividadeAltExec";

const validationSchema = Yup.object().shape({
  textoDocumento: Yup.string().default("").required("Informe o Texto da Atividade").min(1, "Texto muito curto"),
  sequencialAtividade: Yup.string().default(),
  siglaAtividade: Yup.string().default(""),
  cpfRepresentante: Yup.string()
    .default("")
    .test("cpf-valido", "Digite CPF válido", (value, atividade) => {
      if (
        atividade.parent.siglaAtividade === "A4" ||
        atividade.parent.siglaAtividade === "A10" ||
        atividade.parent.siglaAtividade === "A11"
      ) {
        if ((value || []).length !== 14) return false;
      }
      return true;
    }),
  nomeRepresentante: Yup.string()
    .default("")
    .test("nome-valido", "Digite nome do representante", (value, atividade) => {
      if (
        atividade.parent.siglaAtividade === "A4" ||
        atividade.parent.siglaAtividade === "A10" ||
        atividade.parent.siglaAtividade === "A11"
      ) {
        if ((value || []).length === 0) return false;
      }

      return true;
    }),
  cargoRepresentante: Yup.string()
    .default("")
    .test("cargo-valido", "Digite cargo do representante", (value, atividade) => {
      if (
        atividade.parent.siglaAtividade === "A4" ||
        atividade.parent.siglaAtividade === "A10" ||
        atividade.parent.siglaAtividade === "A11"
      ) {
        if ((value || []).length === 0 || value === undefined || value === null) return false;
      }
      return true;
    }),
  nuPaginaEdital: Yup.string()
    .default("")
    .test("numero-valido", "Digite Nº do Diário Oficial", (value, atividade) => {
      if (atividade.parent.siglaAtividade === "A8") {
        if ((value || []).length === 0 || value === undefined || value === null) return false;
      }
      return true;
    }),
  dataComunicado: Yup.date().test(
    "data-valida",
    "Data cadastrada não pode ser nula ou posterior a atual.",
    (value, atividade) => {
      if (
        atividade.parent.siglaAtividade === "A8" ||
        atividade.parent.siglaAtividade === "A10" ||
        atividade.parent.siglaAtividade === "A13"
      ) {
        if ((value || []).length === 0) return false;

        const hoje = new Date();

        if (Date.parse(value) > hoje) return false;
      }
      return true;
    },
  ),
  codigoRastreio: Yup.string()
    .default("")
    .test("codigo-valido", "Digite o código de rastreio", (value, atividade) => {
      if (atividade.parent.siglaAtividade === "A9") {
        if ((value || []).length === 0 || value === undefined || value === null) return false;
      }
      return true;
    }),
  anexos: Yup.array().default([]),
});

const initialValues = {};
validationSchema._nodes.forEach((key) => {
  if (key === "anexos") {
    initialValues[key] = [];
  } else {
    initialValues[key] = "";
  }
});

const TelaProximaAtividadeAltExec = ({ dadosOs, data, atividade, atualizarAlteracao }) => {
  const { request: salvarRequest, data: salvarData, errors: salvarErrors, isLoading: salvarIsLoading } = useApiClient();
  const [sqAndamento, setSqAndamento] = useState(0);
  const [anexosExcluir, setAnexosExcluir] = useState([]);
  const [atualiza, setAtualiza] = useState(false);

  const atualizaAnexosExclusao = (anexos) => {
    setAnexosExcluir(anexos);
  };

  // Se situacaoUltimoAndamento é 'A' significa que a atividade está em andamento,
  // portanto, o sequencialAndamento a ser usado é o que vem no result.
  // Caso a situação seja G, significa que nova atividade será feita, então
  // o sequencialAndamento ainda será incluído, portanto será enviado valor 0.
  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      const parametros = {
        sequencialAlteracaoExecutor: data.sqAlteracaoExecutor,
        sequencialAtividade: atividade?.sequencialAtividade,
        sequencialAndamento: sqAndamento,
        textoDocumento: values.textoDocumento,
        cpfRepresentante: values?.cpfRepresentante,
        nomeRepresentante: values?.nomeRepresentante,
        cargoRepresentante: values?.cargoRepresentante,
        nuPaginaEdital: values?.nuPaginaEdital,
        dataComunicado: values?.dataComunicado,
        codigoRastreio: values?.codigoRastreio,
        anexosInclusao: values?.anexos,
        anexosExclusao: anexosExcluir,
      };

      if (data.situacaoUltimoAndamento === "A") {
        salvarRequest(apiRoutes.fiscalizacao.alteracaoexecutor.alterarAtividade, parametros);
      } else {
        salvarRequest(apiRoutes.fiscalizacao.alteracaoexecutor.incluirAtividade, parametros);
      }
    },
  });

  const { excluirAtividade, excluirErrors, excluirIsLoading } = useExcluirAtividadeAltExec({ data });

  const { assinarAtividade, assinarIsLoading, assinarErrors } = useAssinarAtividadeAltExec({
    dadosOs,
    data,
    atividade,
    formik,
    sqAndamento,
    anexosExcluir,
  });

  const { setSubmitting } = formik;

  useEffect(() => {
    if (data?.situacaoUltimoAndamento === "A") setSqAndamento(data?.sequencialUltimoAndamento);
  }, [data, setSqAndamento]);

  useEffect(() => {
    if (salvarIsLoading) return;

    if (!salvarData && !salvarErrors) return;

    if (salvarData) {
      toast.success("Atividade salva com sucesso!");
      setSqAndamento(salvarData?.result);
      setAtualiza(true);
    }
    setSubmitting(false);
  }, [salvarData, salvarErrors, salvarIsLoading, setSubmitting]);

  useEffect(() => {
    if (atualiza) {
      atualizarAlteracao(data);
    }
  }, [atualiza, atualizarAlteracao, data]);

  const { setValues } = formik;

  useEffect(() => {
    const texto = data?.situacaoUltimoAndamento === "A" ? data?.txDocumentoUltimoAndamento : atividade?.textoDocumento;
    const siglaAtividade = atividade?.siglaAtividade ? atividade?.siglaAtividade : "";
    const cpfRepresentante = data.cpfRepresentante ? data?.cpfRepresentante : "";
    const nomeRepresentante = data.nomeRepresentante ? data?.nomeRepresentante : "";
    const cargoRepresentante = data.cargoRepresentante ? data?.cargoRepresentante : "";
    const nuPaginaEdital = data.nuPaginaEdital ? data?.nuPaginaEdital : "";
    let dataComunicado = data.dataComunicado ? data?.dataComunicado : "";
    const codigoRastreio = data.codigoRastreio ? data?.codigoRastreio : "";

    if (data?.dataCiencia !== null && atividade?.siglaAtividade === "A13" && data?.situacaoUltimoAndamento !== "A") {
      dataComunicado = data.dataCiencia;
    }
    if (
      atividade?.siglaAtividade === "A4" ||
      atividade?.siglaAtividade === "A8" ||
      atividade?.siglaAtividade === "A10"
    ) {
      dataComunicado = new Date().toISOString();
    }

    setValues((curr) => ({
      ...curr,
      textoDocumento: texto,
      siglaAtividade,
      cpfRepresentante,
      nomeRepresentante,
      cargoRepresentante,
      nuPaginaEdital,
      dataComunicado,
      codigoRastreio,
    }));
  }, [
    setValues,
    atividade?.textoDocumento,
    data.situacaoUltimoAndamento,
    data.txDocumentoUltimoAndamento,
    atividade?.siglaAtividade,
    data.cpfRepresentante,
    data.nomeRepresentante,
    data.cargoRepresentante,
    data.nuPaginaEdital,
    data.dataComunicado,
    data.codigoRastreio,
    data.dataCiencia,
  ]);

  if (!atividade?.sequencialAtividade) {
    return null;
  }

  return (
    <>
      <FormAlert
        formik={formik}
        data={salvarData}
        errors={salvarErrors || excluirErrors || assinarErrors}
        isLoading={salvarIsLoading || excluirIsLoading || assinarIsLoading}
        dismissible
      />
      <Form noValidate onSubmit={formik.handleSubmit}>
        <Row className="mt-2">
          <Col>
            <Row className="text-right">
              <Col>
                <Button variant="primary" className="mr-3" onClick={formik.handleSubmit}>
                  Salvar
                </Button>
                <Button
                  variant="primary"
                  className="mr-3"
                  disabled={!formik.isValid}
                  onClick={() => {
                    assinarAtividade();
                  }}
                >
                  Assinar
                </Button>
                {data.situacaoUltimoAndamento === "A" && (
                  <Button variant="primary" className="mr-3" onClick={() => excluirAtividade()}>
                    Excluir
                  </Button>
                )}
              </Col>
            </Row>
            <Row className="mt-2">
              <Col>
                <h5>{atividade.nomeAtividade}</h5>
              </Col>
            </Row>

            <TelaAtividadeAltExec
              formik={formik}
              dadosOs={dadosOs}
              atividade={atividade}
              alteracaoExecutor={data}
              atualizaAnexosExclusao={atualizaAnexosExclusao}
            />
          </Col>
        </Row>
      </Form>
    </>
  );
};

export default TelaProximaAtividadeAltExec;
